import { ref } from "vue";
import { createSharedComposable } from "@vueuse/core";
import { $http } from "@/plugins/api";
import { getApiClientUrl } from "@/config/runtimeConfig";
import type {
  IncidentDetails,
  ReceivedHeader,
  RecipientDetails,
} from "@/modules/PhishTitan/types/IncidentSummary";
import type { Incident } from "@/modules/PhishTitan/types/Incidents";
import type { Collection, Params } from "@/types/api";
import { IncidentAction } from "./Common/IncidentList.types";

const defaultIncidentsList: Collection<Incident> = {
  current_page: 1,
  page_size: 10,
  results: [],
  total: 0,
};

export const useIncidents = createSharedComposable(() => {
  const incidentsList = ref<Collection<Incident>>({ ...defaultIncidentsList });
  const incidentDetails = ref<IncidentDetails | null>(null);
  const receivedHeaders = ref<ReceivedHeader[]>([]);
  const recipientsDetails = ref<RecipientDetails[]>([]);

  const getIncidentsList = async ({
    globalOrganisationID,
    params,
  }: {
    globalOrganisationID: string;
    params: Params;
  }) => {
    incidentsList.value = { ...defaultIncidentsList };

    const paramsWithoutEmptyValues = Object.fromEntries(
      Object.entries(params).filter((param) => param[1]),
    );
    const searchParams = new URLSearchParams(paramsWithoutEmptyValues);

    const res = await $http.get<Collection<Incident>>(
      `${getApiClientUrl()}/v3/pt/history/${globalOrganisationID}/incidents?${searchParams.toString()}`,
    );

    incidentsList.value = res.data;
  };

  const resetIncidentsList = () => {
    incidentsList.value = { ...defaultIncidentsList };
  };

  const getIncident = async ({
    globalOrganisationID,
    ingressID,
    params,
  }: {
    globalOrganisationID: string;
    ingressID: string;
    params: Params;
  }) => {
    incidentDetails.value = null;

    const searchParams = new URLSearchParams(params);

    const res = await $http.get<IncidentDetails>(
      `${getApiClientUrl()}/v3/pt/history/${globalOrganisationID}/incidents/${ingressID}/details?${searchParams.toString()}`,
      {
        _hasCustomErrorHandling: true,
      },
    );
    incidentDetails.value = res.data;
  };

  const getRecipientsDetails = async ({
    globalOrganisationID,
    internetMessageID,
  }: {
    globalOrganisationID: string;
    internetMessageID: string;
  }) => {
    recipientsDetails.value = [];

    const res = await $http.get<RecipientDetails[]>(
      `${getApiClientUrl()}/v3/pt/history/recipients/${globalOrganisationID}/${internetMessageID}`,
    );

    recipientsDetails.value = res.data;
  };

  const getReceivedHeaders = async ({
    customerGlobalAccountID,
    mailbox,
    mailSpMessageID,
  }: {
    customerGlobalAccountID: string;
    mailbox: string;
    mailSpMessageID: string;
  }) => {
    receivedHeaders.value = [];

    const res = await $http.get<ReceivedHeader[]>(
      `${getApiClientUrl()}/v1/pt/mail-message/${customerGlobalAccountID}/${mailbox}/${mailSpMessageID}/headers`,
    );

    receivedHeaders.value = res.data;
  };

  const updateIncidentStatus = async ({
    globalOrganisationID,
    ingressID,
    status,
    params,
  }: {
    globalOrganisationID: string;
    ingressID: string;
    status: IncidentAction;
    params: Params;
  }) => {
    const searchParams = new URLSearchParams(params);

    const res = await $http.put<{ status: string; data: unknown }>(
      `${getApiClientUrl()}/v2/pt/msg/${globalOrganisationID}/incidents/${ingressID}/status?${searchParams.toString()}`,
      { status },
    );

    return { data: res.data.data, status: res.status };
  };

  return {
    incidentsList,
    incidentDetails,
    receivedHeaders,
    recipientsDetails,
    getIncidentsList,
    resetIncidentsList,
    getIncident,
    getRecipientsDetails,
    getReceivedHeaders,
    updateIncidentStatus,
  };
});
